import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Building2,
  CalendarClock,
  LandPlot,
  MapPin,
} from 'lucide-react';
import { defineMessages } from '../../i18n/define';
import type { PlainMessage } from '../../i18n/define';
import { UNIT_LABELS, useT } from '../../i18n/hooks';
import { PageHeader } from '../ui/PageHeader';
import { Card, Grid, Stack } from '../ui/Card';
import { KpiCard } from '../ui/KpiCard';
import { DataTable, type Column } from '../ui/DataTable';
import { Badge } from '../ui/Badge';
import { Disclosure } from '../ui/Disclosure';
import { EmptyState } from '../ui/EmptyState';
import { DataGate } from './parts';
import { CycleChronologySkeleton, CycleExtraWorksSkeleton, CycleKpiSkeleton, CycleWaterSkeleton, CycleWorksTableSkeleton } from './CycleDetailSkeleton';
import {
  daysBetween,
  daysLabel,
} from './CycleTimeline';
import { usePageTrail } from '../shell/ShellContext';
import { useTrackData } from '../data/useTrackData';
import { FILES } from '../data/loader';
import {
  countOf,
  cycleTitle,
  dateShort,
  ha,
  m3,
  m3Compact,
  num,
  pct,
  share,
  windowLabel,
  WORDS,
} from '../lib/format';
import { useAnalysisContext } from '../../analysis/context';
import { useRouteSource } from '../data-source/provider';
import { activeWindows } from './cycleView';
import { AgrocycleSources, AgrocycleUpcoming, useAgrocycle, useStartFlow } from './agrocycle/AgrocyclePlan';
import { CycleFactsRow, CycleHead } from './cycle/CycleHead';
import { agrocycleApi } from './agrocycle/agrocycleApi';
import { useRequest } from '../../api/useRequest';
import { CyclePlanWidget } from './cycle/CyclePlanWidget';
import { CycleWorksMap } from './cycle/CycleWorksMap';
import { PrescriptionsWidget } from '../widgets/prescriptions/PrescriptionsWidget';
import { type CycleOption } from './cycle/CyclePicker';
import { statusLabel, stagesByCampaign, withCycleStages } from './agrocycle/agrocycleView';
import { useAuth } from '../../auth/context';
import { cropName } from '../../lib/crops';
import {
  extraOutsideNote,
  ORIGIN_BADGE_LABEL,
  ORIGIN_DETAIL_LABEL,
  ORIGIN_ICON,
  originBreakdown,
  requirementHref,
  requirementLine,
  useExtraWorksForCycle,
  type ExtraWork,
} from './my/extraWorks';
import type {
  ContourMeta,
  Crop,
  Cycle,
  CycleEvent,
  Farm,
  Meta,
  Obligation,
  WaterFile,
  Work,
} from '../data/types';
import './pages.css';
import './cycles.css';
import { srv } from '../../i18n/serverText';

const m = defineMessages({
  kindStage: { id: 'cycle.detail.event.stage', defaultMessage: 'этап', description: 'Вид события в ленте событий агроцикла: открылся этап; малая подпись под датой' },
  kindCampaign: { id: 'cycle.detail.event.campaign', defaultMessage: 'кампания', description: 'Вид события в ленте событий агроцикла: запущена кампания; малая подпись под датой' },
  kindWindowClose: { id: 'cycle.detail.event.windowClose', defaultMessage: 'закрытие окна', description: 'Вид события в ленте событий агроцикла: закрылось окно работы; малая подпись под датой' },
  kindNote: { id: 'cycle.detail.event.note', defaultMessage: 'примечание', description: 'Вид события в ленте событий агроцикла: примечание; малая подпись под датой' },
  stageCount: {
    id: 'cycle.detail.stageCount',
    defaultMessage: '{count, plural, one {# этап} few {# этапа} many {# этапов} other {# этапа}}',
    description: 'Число этапов агроцикла; {count} — число',
  },
  eventCount: {
    id: 'cycle.detail.eventCount',
    defaultMessage: '{count, plural, one {# событие} few {# события} many {# событий} other {# события}}',
    description: 'Число событий агроцикла; {count} — число',
  },
  rowCount: {
    id: 'cycle.detail.rowCount',
    defaultMessage: '{count, plural, one {# строка} few {# строки} many {# строк} other {# строки}}',
    description: 'Число строк таблицы; {count} — число',
  },
  irrigationCount: {
    id: 'cycle.detail.irrigationCount',
    defaultMessage: '{count, plural, one {# поливу} other {# поливам}}',
    description: 'Число поливов в дательном падеже после «по»: «по 3 поливам»; {count} — число',
  },
  denomWorks: {
    id: 'cycle.detail.denom.works',
    defaultMessage: '{count, plural, one {работа} few {работы} many {работ} other {работы}}',
    description: 'Слово-знаменатель под таблицей работ агроцикла («показано 8 из 22 работ»); без числа',
  },
  denomArea: {
    id: 'cycle.detail.denom.area',
    defaultMessage: '{count, plural, other {га работ}}',
    description: 'Знаменатель полосы выполнения, когда счёт в гектарах работ: «Знаменатель — 120 га работ»; без числа',
  },
  denomFieldWorks: {
    id: 'cycle.detail.denom.fieldWorks',
    defaultMessage: '{count, plural, one {работа на поле} few {работы на полях} many {работ на полях} other {работы на полях}}',
    description: 'Знаменатель полосы выполнения, когда счёт в работах на полях: «Знаменатель — 312 работ на полях»; без числа',
  },
  stageSummaryAreaAxis: {
    id: 'cycle.detail.stageSummary.areaAxis',
    defaultMessage: 'отчитались {pct} площади работ',
    description: 'Часть сводки свёрнутого этапа: доля отчитавшихся по площади работ; {pct} — «78 %»',
  },
  stageSummaryCountAxis: {
    id: 'cycle.detail.stageSummary.countAxis',
    defaultMessage: 'отчитались {pct} работ на полях',
    description: 'Часть сводки свёрнутого этапа: доля отчитавшихся по числу работ на полях; {pct} — «78 %»',
  },
  stageSummaryNotOpened: { id: 'cycle.detail.stageSummary.notOpened', defaultMessage: 'окна ещё не открывались', description: 'Часть сводки свёрнутого этапа: ни одно окно этапа ещё не открывалось' },
  stageSummaryNoObligations: { id: 'cycle.detail.stageSummary.noObligations', defaultMessage: 'работы на полях не назначали', description: 'Часть сводки свёрнутого этапа: окна были, но работ на полях не назначали' },
  stageSummary: {
    id: 'cycle.detail.stageSummary',
    defaultMessage: '{works} · {window} · {axis}',
    description: 'Сводка свёрнутого этапа хронологии: {works} — «5 работ», {window} — окно дат, {axis} — доля отчитавшихся или причина её отсутствия',
  },
  levelDistrict: { id: 'cycle.detail.trail.district', defaultMessage: 'Район', description: 'Уровень в цепочке навигации над заголовком: район' },
  districtName: { id: 'cycle.detail.trail.districtName', defaultMessage: 'Бука', description: 'Название района (Бука) в цепочке навигации; географическое имя — транслитерация' },
  levelSection: { id: 'cycle.detail.trail.section', defaultMessage: 'Раздел', description: 'Уровень в цепочке навигации над заголовком: раздел приложения' },
  levelCycle: { id: 'cycle.detail.trail.cycle', defaultMessage: 'Агроцикл', description: 'Уровень в цепочке навигации над заголовком: агроцикл' },
  trailCycles: { id: 'cycle.detail.trail.cycles', defaultMessage: 'Агроциклы', description: 'Имя раздела «Агроциклы» в цепочке навигации (ссылка на список агроциклов)' },
  cycleFallbackTitle: { id: 'cycle.detail.title.fallback', defaultMessage: 'Агроцикл {id}', description: 'Заголовок страницы, пока название агроцикла не прочитано; {id} — ключ агроцикла из адреса' },
  notFoundTitle: { id: 'cycle.detail.notFound.title', defaultMessage: 'Агроцикла с таким адресом в срезе нет', description: 'Заголовок пустого состояния: адрес агроцикла не найден в данных среза' },
  notFoundReason: {
    id: 'cycle.detail.notFound.reason',
    defaultMessage: 'Данные получены, но агроцикла «{id}» в них нет. Проверьте адрес или переключите трек данных.',
    description: 'Пояснение пустого состояния «агроцикла с таким адресом нет»; {id} — ключ агроцикла из адреса',
  },
  purpose: {
    id: 'cycle.detail.purpose',
    defaultMessage: 'Окно агроцикла {window}. Все величины на экране считаются в работах на полях, контурах и гектарах этого агроцикла.',
    description: 'Подзаголовок страницы агроцикла; {window} — окно дат («1 апр — 15 окт 2026»)',
  },
  templateLink: { id: 'cycle.detail.templateLink', defaultMessage: 'Шаблон агроцикла', description: 'Ссылка в шапке страницы агроцикла: открыть шаблон культуры, по которому построен агроцикл' },
  badgeStatus: { id: 'cycle.detail.badge.status', defaultMessage: 'агроцикл {status}', description: 'Бейдж в шапке страницы агроцикла; {status} — «идёт», «не начат», «завершён»' },
  badgeActive: { id: 'cycle.detail.badge.active', defaultMessage: 'агроцикл идёт', description: 'Бейдж в шапке страницы агроцикла: агроцикл идёт сейчас' },
  badgeClosed: { id: 'cycle.detail.badge.closed', defaultMessage: 'агроцикл закрыт', description: 'Бейдж в шапке страницы агроцикла: агроцикл закрыт' },
  stageNow: { id: 'cycle.detail.badge.stageNow', defaultMessage: 'этап сейчас: {name}', description: 'Бейдж в шапке страницы агроцикла: текущий этап; {name} — название этапа' },
  stageNowClosed: { id: 'cycle.detail.badge.stageNowClosed', defaultMessage: 'этапа сейчас нет — агроцикл закрыт', description: 'Бейдж в шапке страницы агроцикла: агроцикл закрыт, текущего этапа нет' },
  stageNowNone: { id: 'cycle.detail.badge.stageNowNone', defaultMessage: 'этапа сейчас нет', description: 'Бейдж в шапке страницы агроцикла: текущего этапа нет' },
  asOf: { id: 'cycle.detail.badge.asOf', defaultMessage: 'срез {date}', description: 'Малая подпись в шапке страницы агроцикла: дата среза данных; {date} — дата' },
  whatHeader: { id: 'cycle.detail.what.header', defaultMessage: 'шапка агроцикла ждёт {file}', description: 'Что не удалось показать (подставляется в сообщение о пустых данных): шапка агроцикла; {file} — имя файла данных, не переводить' },
  whatChrono: { id: 'cycle.detail.what.chrono', defaultMessage: 'хронология ждёт {file}', description: 'Что не удалось показать (подставляется в сообщение о пустых данных): хронология работ; {file} — имя файла данных, не переводить' },
  whatExtra: { id: 'cycle.detail.what.extra', defaultMessage: 'строка про добровольные работы ждёт {file}', description: 'Что не удалось показать (подставляется в сообщение о пустых данных): добровольные работы; {file} — имя файла данных, не переводить' },
  whatWorks: { id: 'cycle.detail.what.works', defaultMessage: 'таблица работ ждёт {file}', description: 'Что не удалось показать (подставляется в сообщение о пустых данных): таблица работ; {file} — имя файла данных, не переводить' },
  whatEvents: { id: 'cycle.detail.what.events', defaultMessage: 'лента событий ждёт {file}', description: 'Что не удалось показать (подставляется в сообщение о пустых данных): лента событий; {file} — имя файла данных, не переводить' },
  whatWater: { id: 'cycle.detail.what.water', defaultMessage: 'сводка воды ждёт {file}', description: 'Что не удалось показать (подставляется в сообщение о пустых данных): сводка воды; {file} — имя файла данных, не переводить' },
  kpiLand: { id: 'cycle.detail.kpi.land', defaultMessage: 'Земля агроцикла', description: 'Подпись KPI: уникальная земля агроцикла' },
  kpiLandNote: { id: 'cycle.detail.kpi.landNote', defaultMessage: 'земля под работами агроцикла, не посев', description: 'Подпись под KPI «Земля агроцикла»: это земля под работами, а не площадь посева' },
  kpiFarms: { id: 'cycle.detail.kpi.farms', defaultMessage: 'Хозяйства', description: 'Подпись KPI: число хозяйств-участников' },
  kpiFarmsNote: { id: 'cycle.detail.kpi.farmsNote', defaultMessage: 'участники агроцикла', description: 'Подпись под KPI «Хозяйства»' },
  kpiContours: { id: 'cycle.detail.kpi.contours', defaultMessage: 'Контуры', description: 'Подпись KPI: число контуров агроцикла' },
  kpiContoursNote: { id: 'cycle.detail.kpi.contoursNote', defaultMessage: 'на них назначаются работы на полях', description: 'Подпись под KPI «Контуры»' },
  kpiWindows: { id: 'cycle.detail.kpi.windows', defaultMessage: 'Окон идёт сейчас', description: 'Подпись KPI: сколько окон работ плана открыто на дату среза' },
  chronoTitle: { id: 'cycle.detail.chrono.title', defaultMessage: 'Хронология работ', description: 'Заголовок карточки с хронологией работ плана агроцикла' },
  chronoAside: {
    id: 'cycle.detail.chrono.aside',
    defaultMessage:
      '{total, plural, one {# работа} few {# работы} many {# работ} other {# работы}} · {done} закрыто · {active, plural, one {# идёт} other {# идут}} · {upcoming} впереди',
    description: 'Сводка справа в заголовке хронологии; {total} — всего работ плана, {done}/{upcoming} — числа строкой, {active} — сколько окон идёт',
  },
  scrollHint: {
    id: 'cycle.detail.chrono.scrollHint',
    defaultMessage: 'Шкала шире карточки и прокручивается вбок. Колонка «Две оси» примораживается к правому краю — она остаётся на виду при прокрутке.',
    description: 'Подсказка под хронологией: шкала прокручивается вбок; «Две оси» — название правой колонки хронологии',
  },
  conditionalAll: {
    id: 'cycle.detail.chrono.conditionalAll',
    defaultMessage:
      '{count, plural, one {Условна # работа} few {Условны все # работы} many {Условны все # работ} other {Условны все # работы}} агроцикла — пометка у каждой строки ничего не различала бы, поэтому сказана один раз здесь. Условие названо в шаблоне; условная работа назначается не на все контуры агроцикла.',
    description: 'Заметка в легенде хронологии, когда условны все работы; {count} — число работ',
  },
  conditionalSome: {
    id: 'cycle.detail.chrono.conditionalSome',
    defaultMessage:
      'Условных работ {conditional} из {total}: условие названо в шаблоне и показывается подсказкой у подписи «условная». Условная работа назначается не на все контуры агроцикла.',
    description: 'Заметка в легенде хронологии, когда условна часть работ; {conditional}/{total} — числа строкой; «условная» — пометка у строки работы',
  },
  collapse: { id: 'cycle.detail.chrono.collapse', defaultMessage: 'Свернуть до текущего этапа', description: 'Кнопка под хронологией: свернуть остальные этапы' },
  foldNote: {
    id: 'cycle.detail.chrono.foldNote',
    defaultMessage: 'Раскрыты текущий этап и предыдущий. Остальные этапы агроцикла свёрнуты — каждый назван своей величиной, ни один не удалён.',
    description: 'Пояснение под хронологией: какие этапы свёрнуты',
  },
  foldLabel: { id: 'cycle.detail.chrono.foldLabel', defaultMessage: 'Остальные этапы', description: 'Название раскрывающегося блока со свёрнутыми этапами' },
  foldSummary: { id: 'cycle.detail.chrono.foldSummary', defaultMessage: '{stages} · {works}', description: 'Сводка свёрнутого блока: {stages} — «3 этапа», {works} — «12 работ»' },
  showAll: { id: 'cycle.detail.chrono.showAll', defaultMessage: 'Показать все {works}', description: 'Кнопка под хронологией: раскрыть все этапы; {works} — «22 работы»' },
  chronoEmptyTitle: { id: 'cycle.detail.chrono.emptyTitle', defaultMessage: 'Хронология не построена', description: 'Заголовок пустого состояния хронологии' },
  chronoEmptyReason: { id: 'cycle.detail.chrono.emptyReason', defaultMessage: 'работ агроцикла в срезе нет либо их окна не разбираются как даты — шкале нечего показывать', description: 'Причина пустого состояния хронологии' },
  extraTitle: { id: 'cycle.detail.extra.title', defaultMessage: 'Добровольные работы', description: 'Заголовок карточки: добровольные работы хозяйств в агроцикле (по инициативе, по предписаниям, полив вне плана)' },
  extraNone: { id: 'cycle.detail.extra.none', defaultMessage: 'Добровольных работ за агроцикл нет — в срезе не оказалось ни одной такой записи.', description: 'Текст в карточке добровольных работ, когда записей нет' },
  extraSummary: {
    id: 'cycle.detail.extra.summary',
    defaultMessage: 'Добровольных работ за агроцикл: <b>{count}</b> — по инициативе хозяйства {manual}, по предписаниям {finding}, полив вне плана {water}.',
    description: 'Текст в карточке добровольных работ; <b> — выделенное число; {count}/{manual}/{finding}/{water} — числа строкой',
  },
  extraListLabel: { id: 'cycle.detail.extra.listLabel', defaultMessage: 'Список добровольных работ', description: 'Название раскрывающегося блока со списком добровольных работ' },
  extraListSummary: {
    id: 'cycle.detail.extra.listSummary',
    defaultMessage: '{works} · по инициативе хозяйства {manual}, по предписаниям {finding}, полив вне плана {water}',
    description: 'Сводка раскрывающегося списка добровольных работ; {works} — «21 работа», остальное — числа строкой',
  },
  extraEmptyTitle: { id: 'cycle.detail.extra.emptyTitle', defaultMessage: 'Записей нет', description: 'Заголовок пустой таблицы добровольных работ' },
  extraEmptyReason: { id: 'cycle.detail.extra.emptyReason', defaultMessage: 'добровольных работ этого агроцикла в срезе нет', description: 'Причина пустой таблицы добровольных работ' },
  colDate: { id: 'cycle.detail.extra.colDate', defaultMessage: 'Дата', description: 'Колонка таблицы добровольных работ: дата' },
  colFarm: { id: 'cycle.detail.extra.colFarm', defaultMessage: 'Хозяйство', description: 'Колонка таблицы добровольных работ: хозяйство' },
  colContour: { id: 'cycle.detail.extra.colContour', defaultMessage: 'Контур', description: 'Колонка таблицы добровольных работ: контур' },
  colWork: { id: 'cycle.detail.extra.colWork', defaultMessage: 'Работа', description: 'Колонка таблицы добровольных работ: название работы' },
  colOrigin: { id: 'cycle.detail.extra.colOrigin', defaultMessage: 'Происхождение', description: 'Колонка таблицы добровольных работ: откуда взялась работа (по инициативе, по предписанию, полив вне плана)' },
  contourNo: { id: 'cycle.detail.contourNo', defaultMessage: '№ {no}', description: 'Ссылка на контур по его номеру; {no} — номер контура' },
  contourId: { id: 'cycle.detail.contourId', defaultMessage: 'контур #{id}', description: 'Ссылка на контур, когда номера нет; {id} — служебный номер' },
  worksTitle: { id: 'cycle.detail.works.title', defaultMessage: 'Работы агроцикла', description: 'Заголовок карточки с таблицей работ плана агроцикла' },
  worksAside: { id: 'cycle.detail.works.aside', defaultMessage: 'первые {rows}, дальше — «показать все»', description: 'Подпись справа в заголовке таблицы работ; {rows} — «8 строк»; «показать все» — кнопка под таблицей' },
  worksEmptyTitle: { id: 'cycle.detail.works.emptyTitle', defaultMessage: 'Работ в агроцикле нет', description: 'Заголовок пустой таблицы работ агроцикла' },
  worksEmptyReason: { id: 'cycle.detail.works.emptyReason', defaultMessage: 'шаблон агроцикла не развернулся в работы — это пробел генерации, а не ноль', description: 'Причина пустой таблицы работ агроцикла' },
  colWorkStage: { id: 'cycle.detail.works.colWorkStage', defaultMessage: 'Работа и этап', description: 'Колонка таблицы работ: название работы и этап под ним' },
  conditionTitle: { id: 'cycle.detail.works.conditionTitle', defaultMessage: 'Условие: {condition}', description: 'Подсказка у пометки «условная»; {condition} — текст условия из шаблона' },
  conditional: { id: 'cycle.detail.works.conditional', defaultMessage: 'условная', description: 'Пометка у работы плана: назначается только при условии; коротко' },
  colWindow: { id: 'cycle.detail.works.colWindow', defaultMessage: 'Окно', description: 'Колонка таблицы работ: окно выполнения (даты) и состояние под ним' },
  colFieldWorks: { id: 'cycle.detail.works.colFieldWorks', defaultMessage: 'Работ на полях', description: 'Колонка таблицы работ: сколько работ на полях по работе плана (число)' },
  areaOfWork: { id: 'cycle.detail.works.areaOfWork', defaultMessage: '{ha} га работ', description: 'Ячейка таблицы работ: сумма площади работ на полях; {ha} — число строкой' },
  colSplit: { id: 'cycle.detail.works.colSplit', defaultMessage: 'Выполнено, в процессе, без отчёта', description: 'Колонка таблицы работ: три доли — выполнено, в процессе, без отчёта' },
  windowActiveNote: { id: 'cycle.detail.works.windowActive', defaultMessage: 'Окно идёт: пустой отчёт означает «ещё не срок».', description: 'Пояснение под полосой выполнения, когда окно работы идёт' },
  windowUpcomingNote: { id: 'cycle.detail.works.windowUpcoming', defaultMessage: 'Окно впереди: работы на полях назначены, срок не наступил.', description: 'Пояснение под полосой выполнения, когда окно работы впереди' },
  colOverdue: { id: 'cycle.detail.works.colOverdue', defaultMessage: 'После срока', description: 'Колонка таблицы работ: окно закрыто, а отчёта нет' },
  notMeasured: { id: 'cycle.detail.works.notMeasured', defaultMessage: 'не измерено', description: 'Ячейка колонки «После срока»: сервер не измерил' },
  withoutReport: { id: 'cycle.detail.works.withoutReport', defaultMessage: '{value} без отчёта', description: 'Малая подпись под числом в колонке «После срока»; {value} — «12 работ на полях» или «5 %»' },
  colLate: { id: 'cycle.detail.works.colLate', defaultMessage: 'Поздние', description: 'Колонка таблицы работ: отчёты, поданные после окна' },
  mapTitle: { id: 'cycle.detail.map.title', defaultMessage: 'Карта агроцикла', description: 'Заголовок карточки с картой агроцикла' },
  mapCaptionPicked: { id: 'cycle.detail.map.captionPicked', defaultMessage: 'Цвет — состояние работы на поле по работе «{name}»', description: 'Подпись под картой: что значит цвет контура; {name} — название работы плана' },
  mapCaptionLoading: { id: 'cycle.detail.map.captionLoading', defaultMessage: 'Работы на полях ещё читаются — пока показана культура размещения', description: 'Подпись под картой, пока данные грузятся' },
  mapCaptionCrop: { id: 'cycle.detail.map.captionCrop', defaultMessage: 'Показана культура размещения: раскрасить по работе нечем', description: 'Подпись под картой, когда раскрашивать по работе нечем' },
  pickTitle: { id: 'cycle.detail.map.pickTitle', defaultMessage: 'Раскрасить по работе', description: 'Заголовок блока выбора работы справа от карты' },
  pickActive: { id: 'cycle.detail.map.pickActive', defaultMessage: '{name} · идёт', description: 'Кнопка выбора работы для раскраски карты: окно идёт; {name} — название работы' },
  pickDone: { id: 'cycle.detail.map.pickDone', defaultMessage: '{name} · закрыто', description: 'Кнопка выбора работы для раскраски карты: окно закрыто; {name} — название работы' },
  pickNote: { id: 'cycle.detail.map.pickNote', defaultMessage: 'Раскрасить можно по идущим окнам и по последнему закрытому: у остальных состояние работы на поле уже история, а не картина дня.', description: 'Пояснение под кнопками выбора работы для раскраски карты' },
  selTitle: { id: 'cycle.detail.map.selTitle', defaultMessage: 'Что в выборке', description: 'Заголовок блока справа от карты: что попало в выборку выбранной работы' },
  selPicked: {
    id: 'cycle.detail.map.selPicked',
    defaultMessage: 'Работа «{name}», окно {window}: {works} на {ha} га. Контур без заливки в выборку этой работы не входит. {tail}',
    description: 'Описание выборки выбранной работы; {name} — название работы, {window} — окно дат, {works} — «312 работ на полях», {ha} — число, {tail} — продолжение (одна из трёх фраз ниже)',
  },
  selTailApi: { id: 'cycle.detail.map.selTailApi', defaultMessage: 'На кадре — {contours} этой работы; статус контура свёрнут сервером по худшему состоянию работы на поле.', description: 'Продолжение описания выборки; {contours} — «120 контуров»' },
  selTailRest: { id: 'cycle.detail.map.selTailRest', defaultMessage: 'Остальные {contours} агроцикла работа не затрагивает.', description: 'Продолжение описания выборки; {contours} — «40 контуров»' },
  selTailAll: { id: 'cycle.detail.map.selTailAll', defaultMessage: 'Работа назначена на все контуры агроцикла.', description: 'Продолжение описания выборки: работа назначена на все контуры' },
  selLoading: { id: 'cycle.detail.map.selLoading', defaultMessage: 'Работы на полях агроцикла ещё читаются — пока на кадре культура размещения по всему району.', description: 'Описание выборки, пока данные грузятся' },
  selNoWork: { id: 'cycle.detail.map.selNoWork', defaultMessage: 'Работы агроцикла не прочитаны: раскрашивать по работе нечем, показана культура размещения по всему району.', description: 'Описание выборки: работы не загрузились' },
  selNoStatusApi: { id: 'cycle.detail.map.selNoStatusApi', defaultMessage: 'Статус контуров по работе сервер не отдал{reason}: показана культура размещения по всему району.', description: 'Описание выборки: сервер не вернул статусы; {reason} — « (текст ошибки)» с пробелом впереди или пусто' },
  selNoStatusReason: { id: 'cycle.detail.map.selNoStatusReason', defaultMessage: ' ({message})', description: 'Вставка с текстом ошибки сервера; {message} — текст ошибки; начинается с пробела' },
  selNoStatus: { id: 'cycle.detail.map.selNoStatus', defaultMessage: 'Работы на полях агроцикла не прочитаны: статус работы на карте показать нечем, показана культура размещения по всему району.', description: 'Описание выборки: работы на полях не загрузились' },
  eventsTitle: { id: 'cycle.detail.events.title', defaultMessage: 'Хронология событий агроцикла', description: 'Заголовок карточки с лентой событий агроцикла' },
  eventsAside: { id: 'cycle.detail.events.aside', defaultMessage: 'последние {count}', description: 'Подпись справа в заголовке ленты событий; {count} — сколько событий показано' },
  eventsEmptyTitle: { id: 'cycle.detail.events.emptyTitle', defaultMessage: 'Событий нет', description: 'Заголовок пустой ленты событий агроцикла' },
  eventsEmptyReason: { id: 'cycle.detail.events.emptyReason', defaultMessage: 'данные о событиях получены, но записей по этому агроциклу в них нет', description: 'Причина пустой ленты событий' },
  eventsAllLabel: { id: 'cycle.detail.events.allLabel', defaultMessage: 'Вся история агроцикла', description: 'Название раскрывающегося блока со всей лентой событий' },
  eventsAllSummary: { id: 'cycle.detail.events.allSummary', defaultMessage: '{events}, {window}', description: 'Сводка раскрывающегося блока событий; {events} — «24 события», {window} — окно дат' },
  waterTitle: { id: 'cycle.detail.water.title', defaultMessage: 'Вода агроцикла', description: 'Заголовок карточки с водой агроцикла (запрошено и подано)' },
  waterRequested: { id: 'cycle.detail.water.requested', defaultMessage: 'Запрошено', description: 'Факт в карточке воды: сколько воды запросили хозяйства' },
  waterDelivered: { id: 'cycle.detail.water.delivered', defaultMessage: 'Подано', description: 'Факт в карточке воды: сколько воды подали' },
  waterCoverage: { id: 'cycle.detail.water.coverage', defaultMessage: 'Покрытие заявок', description: 'Факт в карточке воды: доля поданной воды от запрошенной' },
  waterRequests: { id: 'cycle.detail.water.requests', defaultMessage: 'Заявок', description: 'Факт в карточке воды: число заявок на воду' },
  waterRequestsValue: { id: 'cycle.detail.water.requestsValue', defaultMessage: '{requests} по {irrigations}', description: 'Значение факта «Заявок»; {requests} — число заявок, {irrigations} — «3 поливам»' },
  waterListLabel: { id: 'cycle.detail.water.listLabel', defaultMessage: 'Поливы агроцикла', description: 'Название раскрывающегося блока со списком поливов' },
  waterListSummary: { id: 'cycle.detail.water.listSummary', defaultMessage: '{works}, {delivered} подано', description: 'Сводка списка поливов; {works} — «5 работ», {delivered} — объём воды с единицей' },
  waterRow: { id: 'cycle.detail.water.row', defaultMessage: '{delivered} из {requested} · {pct}', description: 'Строка полива: подано из запрошенного и доля; объёмы с единицей, {pct} — «87,5 %»' },
  waterEmptyTitle: { id: 'cycle.detail.water.emptyTitle', defaultMessage: 'Поливов в агроцикле нет', description: 'Заголовок пустого состояния воды' },
  waterEmptyReason: { id: 'cycle.detail.water.emptyReason', defaultMessage: 'в данных нет ни одной строки воды — либо у культуры нет поливных работ, либо заявки ещё не заводились', description: 'Причина пустого состояния воды' },
  waterBarsAria: { id: 'cycle.detail.water.barsAria', defaultMessage: 'Подано воды по поливам агроцикла: {list}', description: 'aria-label диаграммы столбиков воды; {list} — перечень «полив — объём» через «;»' },
  waterBarsItem: { id: 'cycle.detail.water.barsItem', defaultMessage: '{name} — {value}', description: 'Элемент перечня в aria-label диаграммы воды: название полива и объём' },
  waterBarTitle: { id: 'cycle.detail.water.barTitle', defaultMessage: '{name}: подано {delivered} из {requested}', description: 'Подсказка столбика воды; объёмы с единицей' },
  waterChartNote: { id: 'cycle.detail.water.chartNote', defaultMessage: 'Столбец — подано по поливу, слева направо по порядку работ. Норма на гектар — допущение прототипа, не утверждённая карта.', description: 'Подпись под столбиками воды' },
  footer: { id: 'cycle.detail.footer', defaultMessage: 'До конца окна агроцикла: {left}. Работы, чьё окно ещё не открылось, молчанием не считаются.', description: 'Подпись внизу страницы идущего агроцикла; {left} — «12 дней» или «срок не посчитан»' },
  footerUnknown: { id: 'cycle.detail.footerUnknown', defaultMessage: 'срок не посчитан', description: 'Подстановка в «До конца окна агроцикла: …», когда срок посчитать нечем' },
});

const EVENT_KIND: Record<CycleEvent['kind'], PlainMessage> = {
  stage_open: m.kindStage,
  campaign: m.kindCampaign,
  window_close: m.kindWindowClose,
  note: m.kindNote,
};

interface WaterRowAgg {
  workId: string;
  name: string;
  seq: number;
  requested: number;
  delivered: number;
  rows: number;
}

export function CycleDetail() {
  const t = useT();
  const { id = '' } = useParams();

  const meta = useTrackData<Meta>(FILES.meta);
  const cycles = useTrackData<Cycle[]>(FILES.cycles);
  const crops = useTrackData<Crop[]>(FILES.crops);
  const works = useTrackData<Work[]>(id ? FILES.works(id) : null);
  const obligations = useTrackData<Obligation[]>(id ? FILES.obligations(id) : null);
  const water = useTrackData<WaterFile>(id ? FILES.water(id) : null);
  const events = useTrackData<CycleEvent[]>(id ? FILES.events(id) : null);
  /* На каноне обязательств цикла списком нет (obligations.ts): статус
     контуров по работе читается свёрнутым с сервера (agroai-6al.27). */
  const onApi = useRouteSource()?.source === 'api';
  /* Блок добровольных работ рисуется только на фикстурах: на каноне его
     справочники (все хозяйства и 10 000 контуров района, ~6 МБ) не грузим —
     это был самый тяжёлый лишний обход страницы (agroai-0e8, 08.10). */
  const extraWorks = useExtraWorksForCycle(!onApi && id ? id : null);
  const farms = useTrackData<Farm[]>(onApi ? null : FILES.farms);
  const extraContours = useTrackData<ContourMeta[]>(onApi ? null : FILES.contours);

  const cycle = (cycles.data ?? []).find((c) => c.id === id) ?? null;
  const asOf = meta.data?.as_of ?? null;
  const title = cycle ? cycleTitle(cycle, crops.data) : t(m.cycleFallbackTitle, { id });
  /* Дата среза шапки — не ждёт списка циклов: срез из адреса, иначе
     сегодня (так же разрешает её сервер, `_horizon`). */
  const slice = useAnalysisContext();
  const headAsOf = asOf ?? slice.asOf ?? new Date().toISOString().slice(0, 10);


  /* ------------------------------------------------------------ работы */

  /* Строка агроцикла с сервера (ядро S4): этапы — строки шаблона/цикла, а не
     группировка справочника стадий; на фикстурах её нет — прежний путь. */
  const agroState = useAgrocycle(onApi && id ? id : null);
  const agro = agroState.detail;
  /* Лёгкая шапка (`brief=true`, agroai-0e8): строка, план и факты размещения
     без хода по этапам — ~0,35 с против ~1,2 с полной строки на холодном
     кэше. Полная строка заменяет её, как только придёт. */
  const brief = useRequest(
    () => (onApi && id ? agrocycleApi.detail(id, { ...slice.params, brief: true }) : Promise.resolve(null)),
    [onApi, id, JSON.stringify(slice.params)],
  );
  const headRow = agro ?? brief.data ?? null;
  /* Название — из строки цикла, как только она есть: список циклов
     (`cycles.json`) на холодном кэше приходит заметно позже. */
  const headTitle = cycle
    ? title
    : onApi && headRow
      ? cycleTitle({ id: headRow.key, crop: headRow.crop_code, season_label: headRow.season_label }, [{ id: headRow.crop_code, name: cropName(headRow.crop_code, headRow.crop_name) } as Crop])
      : title;

  usePageTrail([
    // i18n-ignore: запасное имя района — собственное название, а не текст интерфейса
    { level: t(m.levelDistrict), name: srv(meta.data?.district) ?? t(m.districtName), to: '/' },
    { level: t(m.levelSection), name: t(m.trailCycles), to: '/cycles' },
    { level: t(m.levelCycle), name: cycle || (onApi && headRow) ? headTitle : id },
  ]);
  const canEdit = useAuth().principal?.can_edit_agrocycle ?? false;
  const start = useStartFlow(canEdit && agro?.status === 'not_started' ? agro : null, agroState.params, (d, note) => agroState.update(d, note));
  const [editSignal, setEditSignal] = useState(0);
  /* Выбор цикла — из того же набора `cycles.json` (`GET /agrocycles`), что и
     название: второй такой же запрос на холодном кэше считался дважды. */
  const pickerOptions: CycleOption[] = (cycles.data ?? []).map((c) => ({
    key: c.id,
    name: cycleTitle(c, crops.data),
    note: c.status === 'closed' ? statusLabel('finished') : c.stage_now ? statusLabel('started') : undefined,
  }));
  const cycleStages = useMemo(() => stagesByCampaign(agro?.plan.stages), [agro]);
  const ordered = useMemo(
    () => withCycleStages([...(works.data ?? [])].sort((a, b) => a.seq - b.seq), cycleStages),
    [works.data, cycleStages],
  );


  /* ------------------------------------------------------ обязательства */

  /**
   * Файл обязательств — миллионы символов и десятки тысяч строк. Разбор
   * делается один раз: индекс по работам плюс список контуров цикла.
   * Смена работы в селекторе карты трогает только одну корзину.
   */
  const index = useMemo(() => {
    const data = obligations.data;
    if (!data) return null;

    const byWork = new Map<string, Obligation[]>();
    const contourIds: string[] = [];
    const seen = new Set<string>();

    for (const row of data) {
      let bucket = byWork.get(row.work_id);
      if (!bucket) {
        bucket = [];
        byWork.set(row.work_id, bucket);
      }
      bucket.push(row);
      if (!seen.has(row.contour_id)) {
        seen.add(row.contour_id);
        contourIds.push(row.contour_id);
      }
    }

    return { byWork, contourIds };
  }, [obligations.data]);

  /* -------------------------------------------------------------- вода */

  const waterRows = useMemo(() => {
    const file = water.data;
    if (!file) return null;
    const agg = new Map<string, WaterRowAgg>();
    for (const row of file.rows) {
      let hit = agg.get(row.work_id);
      if (!hit) {
        const work = ordered.find((w) => w.id === row.work_id) ?? null;
        hit = {
          workId: row.work_id,
          name: work?.name ?? row.work_id,
          seq: work?.seq ?? Number.MAX_SAFE_INTEGER,
          requested: 0,
          delivered: 0,
          rows: 0,
        };
        agg.set(row.work_id, hit);
      }
      hit.requested += row.requested_m3;
      hit.delivered += row.delivered_m3;
      hit.rows += 1;
    }
    return [...agg.values()].sort((a, b) => a.seq - b.seq);
  }, [water.data, ordered]);

  /** Масштаб столбиков воды: самый большой полив задаёт высоту. */
  const waterMax = useMemo(
    () => Math.max(1, ...(waterRows ?? []).map((row) => row.delivered)),
    [waterRows],
  );

  /* ------------------------------------------------------------ события */

  const eventFeed = useMemo(
    () => [...(events.data ?? [])].sort((a, b) => b.date.localeCompare(a.date)),
    [events.data],
  );

  /* ------------------------------------------------------- внеплановые работы */

  const farmNameById = useMemo(
    () => new Map((farms.data ?? []).map((f) => [f.id, f.name])),
    [farms.data],
  );
  const contourNoById = useMemo(
    () => new Map((extraContours.data ?? []).map((c) => [c.id, c.no])),
    [extraContours.data],
  );

  const extraColumns: Column<ExtraWork>[] = [
    { key: 'date', header: t(m.colDate), width: '130px', render: (row) => dateShort(row.date) },
    {
      key: 'farm',
      header: t(m.colFarm),
      render: (row) => <Link to={`/farms/${row.farm_id}`}>{farmNameById.get(row.farm_id) ?? row.farm_id}</Link>,
    },
    {
      key: 'contour',
      header: t(m.colContour),
      render: (row) => (
        <Link to={`/contours/${row.contour_id}`}>{contourNoById.get(row.contour_id)
            ? t(m.contourNo, { no: String(contourNoById.get(row.contour_id)) })
            : t(m.contourId, { id: String(row.contour_id) })}</Link>
      ),
    },
    {
      key: 'name',
      header: t(m.colWork),
      render: (row) => {
        const requirement = requirementLine(row);
        return (
          <>
            {row.name}
            {row.note ? <div className="t-caption">{row.note}</div> : null}
            {/* Ссылка на само предписание: цепочка «находка → предписание →
                работа» живёт в блоке «Карантин» паспорта контура. */}
            {requirement ? (
              <div className="t-caption">
                <Link to={requirementHref(row)}>{requirement}</Link>
              </div>
            ) : null}
          </>
        );
      },
    },
    {
      key: 'origin',
      header: t(m.colOrigin),
      render: (row) => (
        <>
          <Badge tone="copper" icon={ORIGIN_ICON[row.origin]}>
            {ORIGIN_BADGE_LABEL[row.origin]}
          </Badge>
          <div className="t-caption">{ORIGIN_DETAIL_LABEL[row.origin]}</div>
        </>
      ),
    },
  ];

  /**
   * Дата — первая колонка, значит и порядок по ней: файл сгруппирован по
   * хозяйству, и «показано 8 из 21» отдавало читателю произвольные восемь.
   * Свежее сверху — как в хронологии кабинета фермера.
   */
  const extraRowsByDate = (rows: ExtraWork[]): ExtraWork[] =>
    [...rows].sort((a, b) => b.date.localeCompare(a.date) || a.id.localeCompare(b.id));

  /* ------------------------------------------------- подробная хронология */

  const feed = (items: CycleEvent[]) => (
    <ul className="cyc-events">
      {items.map((event, i) => (
        <li className="cyc-event" key={`${event.date}-${i}`}>
          <span className="cyc-event__date">
            {dateShort(event.date)}
            <div className="cyc-event__kind">{t(EVENT_KIND[event.kind])}</div>
          </span>
          <span className="cyc-event__main">
            <span className="cyc-event__title">{event.title}</span>
            <span className="cyc-event__body">{event.body}</span>
          </span>
        </li>
      ))}
    </ul>
  );

  // Лента событий — внутри режима «Подробно» виджета плана (agroai-0e8):
  // диаграмма окон по раскрытию убрана, окна работ рисует сам виджет.
  const eventsBlock = (
    <Stack tight>
      {events.state === 'ready' && eventFeed.length > 0 ? (
        <div data-guide="events">
          <h3 className="t-label">{t(m.eventsTitle)}</h3>
          {feed(eventFeed.slice(0, 8))}
          {eventFeed.length > 8 ? (
            <Disclosure
              label={t(m.eventsAllLabel)}
              summary={t(m.eventsAllSummary, {
                events: t(m.eventCount, { count: eventFeed.length }),
                window: windowLabel(eventFeed[eventFeed.length - 1]!.date, eventFeed[0]!.date),
              })}
            >
              {feed(eventFeed.slice(8))}
            </Disclosure>
          ) : null}
        </div>
      ) : null}
    </Stack>
  );

  /* ------------------------------------------------------------- рендер */

  const windowsNow = cycle ? activeWindows(cycle, works.state === 'ready' ? ordered : null) : null;

  if (cycles.state === 'ready' && !cycle) {
    return (
      <Stack>
        <PageHeader level="district" title={t(m.cycleFallbackTitle, { id })} />
        <EmptyState
          title={t(m.notFoundTitle)}
          reason={t(m.notFoundReason, { id })}
        />
      </Stack>
    );
  }

  const fallback = !onApi && cycle
    ? {
        title,
        crop: (crops.data ?? []).find((c) => c.id === cycle.crop)?.name ?? cycle.crop,
        season: cycle.season_label,
        from: cycle.window.from,
        to: cycle.window.to,
        status: cycle.status === 'active' ? ('started' as const) : ('finished' as const),
        templateId: cycle.template_id,
      }
    : null;

  return (
    <Stack>
      <CycleHead
        id={id}
        detail={onApi ? headRow : null}
        fallback={fallback}
        title={headTitle}
        asOf={headAsOf}
        options={pickerOptions}
        start={agro?.status === 'not_started' && canEdit ? start.trigger : null}
        onEdit={canEdit && agro?.status === 'started' ? () => setEditSignal((n) => n + 1) : null}
      />
      {start.panel}

      {onApi ? (
        headRow ? <CycleFactsRow detail={headRow} asOf={headAsOf} /> : agroState.req.error ? null : <CycleKpiSkeleton />
      ) : null}

      {onApi ? null : <DataGate res={cycles} what={t(m.whatHeader, { file: 'cycles.json' })} skeleton={<CycleKpiSkeleton />}>
        {() =>
          cycle ? (
            <div data-guide="kpi"><Grid cols={4}>
              <KpiCard
                label={t(m.kpiLand)}
                value={ha(cycle.area_ha)}
                unit={t(UNIT_LABELS.ha)}
                icon={LandPlot}
                note={t(m.kpiLandNote)}
              />
              <KpiCard
                label={t(m.kpiFarms)}
                value={num(cycle.farms)}
                icon={Building2}
                note={t(m.kpiFarmsNote)}
              />
              <KpiCard
                label={t(m.kpiContours)}
                value={num(cycle.contours)}
                icon={MapPin}
                note={t(m.kpiContoursNote)}
              />
              <KpiCard
                label={t(m.kpiWindows)}
                value={num(windowsNow!.value)}
                icon={CalendarClock}
                variant={(windowsNow!.value ?? 0) > 0 ? 'hero' : 'neutral'}
                note={windowsNow!.note}
              />
            </Grid></div>
          ) : null
        }
      </DataGate>}

      {onApi && agro ? <div data-guide="upcoming"><AgrocycleUpcoming detail={agro} /></div> : null}

      <div data-guide="timeline">
        <DataGate res={works} what={t(m.whatChrono, { file: FILES.works(id) })} skeleton={<CycleChronologySkeleton />}>
          {() => (
            <CyclePlanWidget
              cycleId={id}
              agro={onApi ? agroState : null}
              works={ordered}
              asOf={asOf}
              editSignal={editSignal}
              events={events.state === 'ready' && eventFeed.length > 0 ? eventsBlock : null}
            />
          )}
        </DataGate>
      </div>

      <div data-guide="works">
        <DataGate res={works} what={t(m.whatWorks, { file: FILES.works(id) })} skeleton={<CycleWorksTableSkeleton />}>
          {() => (
            <CycleWorksMap
              cycleId={id}
              works={ordered}
              crops={crops.data ?? undefined}
              onApi={onApi}
              index={onApi ? null : index}
              hrefFor={(work) => `/cycles/${id}/works/${work.id}`}
              asOf={headAsOf}
              planAhead={agro?.plan.stages.flatMap((stage) =>
                stage.works
                  .filter((w) => w.state === 'ahead')
                  .map((w) => ({ id: w.id, label: w.label, stage: stage.name, window_from: w.window_from, window_to: w.window_to })),
              )}
            />
          )}
        </DataGate>
      </div>

      {onApi && agro ? <div data-guide="sources"><AgrocycleSources state={agroState} /></div> : null}

      {/* Предписания карантина — работы, созданные карантином: в процент выполнения плана не входят (S4, К9). */}
      <div data-guide="prescriptions"><PrescriptionsWidget scope={{ cycle: id }} /></div>

      {onApi ? null : <div data-guide="extra-works"><Card size="lg" className="cyc-wide" title={t(m.extraTitle)}>
        <DataGate res={extraWorks} what={t(m.whatExtra, { file: `extra-works-${id}.json` })} skeleton={<CycleExtraWorksSkeleton />}>
          {(rows) => {
            const b = originBreakdown(rows);
            return rows.length === 0 ? (
              <p className="t-body">{t(m.extraNone)}</p>
            ) : (
              <Stack tight>
                <p className="t-body">
                  {t(m.extraSummary, {
                    b: (c) => <strong className="num">{c}</strong>,
                    count: num(rows.length),
                    manual: num(b.manual),
                    finding: num(b.q_finding),
                    water: num(b.unplanned_water),
                  })}
                </p>
                <p className="t-caption">{extraOutsideNote()}</p>
                <Disclosure
                  label={t(m.extraListLabel)}
                  summary={t(m.extraListSummary, {
                    works: countOf(rows.length, WORDS.work),
                    manual: num(b.manual),
                    finding: num(b.q_finding),
                    water: num(b.unplanned_water),
                  })}
                >
                  <DataTable
                    columns={extraColumns}
                    rows={extraRowsByDate(rows)}
                    getKey={(row) => row.id}
                    empty={{
                      title: t(m.extraEmptyTitle),
                      reason: t(m.extraEmptyReason),
                    }}
                  />
                </Disclosure>
              </Stack>
            );
          }}
        </DataGate>
      </Card></div>}

      {onApi && water.state === 'missing' ? null : <div data-guide="water"><Card size="lg" title={t(m.waterTitle)}>
        <DataGate res={water} what={t(m.whatWater, { file: FILES.water(id) })} skeleton={<CycleWaterSkeleton />}>
          {(file) => (
            <div className="cyc-water">
              <Stack tight>
                <ul className="facts">
                  <li className="facts__row">
                    <span className="facts__key">{t(m.waterRequested)}</span>
                    <span className="facts__val">{m3(file.summary.requested_m3)}</span>
                  </li>
                  <li className="facts__row">
                    <span className="facts__key">{t(m.waterDelivered)}</span>
                    <span className="facts__val">{m3(file.summary.delivered_m3)}</span>
                  </li>
                  <li className="facts__row">
                    <span className="facts__key">{t(m.waterCoverage)}</span>
                    <span className="facts__val">{pct(file.summary.coverage_pct, 1)}</span>
                  </li>
                  <li className="facts__row">
                    <span className="facts__key">{t(m.waterRequests)}</span>
                    <span className="facts__val">
                      {t(m.waterRequestsValue, {
                        requests: num(file.summary.requests),
                        irrigations: t(m.irrigationCount, { count: waterRows?.length ?? 0 }),
                      })}
                    </span>
                  </li>
                </ul>

                {waterRows && waterRows.length > 0 ? (
                  <Disclosure
                    label={t(m.waterListLabel)}
                    summary={t(m.waterListSummary, {
                      works: countOf(waterRows.length, WORDS.work),
                      delivered: m3(file.summary.delivered_m3),
                    })}
                  >
                    <ul className="facts">
                      {waterRows.map((row) => (
                        <li className="facts__row" key={row.workId}>
                          <span className="facts__key">
                            <Link to={`/cycles/${id}/works/${row.workId}`}>{row.name}</Link>
                          </span>
                          <span className="facts__val">
                            {t(m.waterRow, {
                              delivered: m3(row.delivered),
                              requested: m3(row.requested),
                              pct: pct(share(row.delivered, row.requested), 1),
                            })}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </Disclosure>
                ) : (
                  <EmptyState
                    compact
                    title={t(m.waterEmptyTitle)}
                    reason={t(m.waterEmptyReason)}
                  />
                )}
              </Stack>

              {waterRows && waterRows.length > 0 ? (
                <div className="cyc-water__chart">
                  <div
                    className="cyc-waterbars"
                    role="img"
                    aria-label={t(m.waterBarsAria, {
                      list: waterRows.map((row) => t(m.waterBarsItem, { name: row.name, value: m3(row.delivered) })).join('; '),
                    })}
                  >
                    {waterRows.map((row) => (
                      <div
                        className="cyc-waterbar"
                        key={row.workId}
                        title={t(m.waterBarTitle, { name: row.name, delivered: m3(row.delivered), requested: m3(row.requested) })}
                      >
                        {/* Высота даёт только порядок величины — число стоит над
                            столбиком, а точное значение живёт в подсказке. */}
                        <span className="cyc-waterbar__val">{m3Compact(row.delivered)}</span>
                        <span
                          className="cyc-waterbar__col"
                          style={{
                            height: `${Math.max(4, Math.round((row.delivered / waterMax) * 72))}px`,
                          }}
                        />
                        <span className="cyc-waterbar__name">{row.name}</span>
                      </div>
                    ))}
                  </div>
                  <span className="t-caption">
                    {t(m.waterChartNote)}
                  </span>
                </div>
              ) : null}
            </div>
          )}
        </DataGate>
      </Card></div>}

      {cycle && cycle.status === 'active' && asOf ? (
        <div className="t-caption">
          {t(m.footer, {
            left: (() => {
              const left = daysBetween(asOf, cycle.window.to);
              return left === null ? t(m.footerUnknown) : daysLabel(Math.max(0, left));
            })(),
          })}
        </div>
      ) : null}
    </Stack>
  );
}
